// AccountsPage: accounts aanmaken en aanpassen (alleen voor beheerders).
// Nieuwe accounts en herstelde wachtwoorden krijgen een tijdelijk wachtwoord
// dat hier één keer getoond wordt; de gebruiker kiest bij de eerste login een
// eigen wachtwoord.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '@rvdh/react-auth-context';
import AppLayout from '../components/AppLayout.jsx';
import { api, endpoints } from '../api/client.js';

const EMPTY = { username: '', name: '', email: '', is_admin: false, is_active: true };

function errorText(err, fallback) {
    if (!err?.statusCode) { return 'Geen verbinding met de server. Probeer het opnieuw.'; }
    if (err.statusCode === 403) { return 'Deze pagina is alleen voor beheerders.'; }
    return err.message && !/^HTTP/.test(err.message) ? err.message : fallback;
}

function formatDate(value) {
    if (!value) { return 'nog niet'; }
    return new Date(value).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
}

function PasswordDialog({ info, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try { await navigator.clipboard.writeText(info.password); setCopied(true); } catch { /* geen klembord */ }
    };
    return (
        <Dialog open={Boolean(info)} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Tijdelijk wachtwoord</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Geef dit wachtwoord door aan <strong>{info?.name}</strong> (gebruikersnaam <strong>{info?.username}</strong>).
                    Bij de eerste login kiest {info?.name} een eigen wachtwoord. Na sluiten is het hier niet meer te zien.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ fontFamily: 'monospace', fontSize: '1.3rem', p: 1.5, bgcolor: 'grey.100', borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                        {info?.password}
                    </Box>
                    <Tooltip title={copied ? 'Gekopieerd' : 'Kopiëren'}>
                        <IconButton onClick={copy} aria-label="Kopiëren"><ContentCopyIcon /></IconButton>
                    </Tooltip>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Sluiten</Button>
            </DialogActions>
        </Dialog>
    );
}

function AccountDialog({ account, isSelf, onClose, onSaved, onPassword }) {
    const isNew = !account?.id;
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setForm(account ? { ...EMPTY, ...account } : EMPTY);
        setError(null);
    }, [account]);

    const set = (key) => (ev) => {
        const value = ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            if (isNew) {
                const res = await api.post(endpoints.adminUsers, {
                    username: form.username, name: form.name, email: form.email, is_admin: form.is_admin,
                });
                onSaved(res.user);
                onPassword({ name: res.user.name, username: res.user.username, password: res.temporary_password });
            } else {
                const body = { name: form.name, email: form.email };
                if (!isSelf) { body.is_active = form.is_active; body.is_admin = form.is_admin; }
                const res = await api.patch(endpoints.adminUser(account.id), body);
                onSaved(res.user);
            }
        } catch (err) {
            setError(errorText(err, 'Opslaan is mislukt.'));
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await api.post(endpoints.adminResetPassword(account.id), {});
            onSaved({ ...account, password_change_required: true });
            onPassword({ name: account.name, username: account.username, password: res.temporary_password });
        } catch (err) {
            setError(errorText(err, 'Wachtwoord herstellen is mislukt.'));
        } finally {
            setSaving(false);
        }
    };

    const canSave = form.name.trim().length >= 2 && form.email.includes('@') && (!isNew || form.username.trim().length >= 2);

    return (
        <Dialog open={Boolean(account)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isNew ? 'Nieuw account' : `Account ${account?.username}`}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Gebruikersnaam"
                        value={form.username}
                        onChange={set('username')}
                        disabled={!isNew}
                        helperText={isNew ? 'Kleine letters, cijfers, punt of streepje. Later niet te wijzigen.' : 'Niet te wijzigen'}
                        autoFocus={isNew}
                        autoComplete="off"
                    />
                    <TextField label="Naam" value={form.name} onChange={set('name')} autoComplete="off" />
                    <TextField label="E-mailadres" type="email" value={form.email} onChange={set('email')} autoComplete="off" />
                    <FormControlLabel
                        control={<Switch checked={form.is_admin} onChange={set('is_admin')} disabled={isSelf} />}
                        label="Beheerder (mag accounts aanmaken en aanpassen)"
                    />
                    {!isNew && (
                        <FormControlLabel
                            control={<Switch checked={form.is_active} onChange={set('is_active')} disabled={isSelf} />}
                            label={form.is_active ? 'Actief' : 'Uitgeschakeld (kan niet inloggen)'}
                        />
                    )}
                    {isSelf && (
                        <Typography variant="body2" color="text.secondary">
                            Je eigen account kun je niet uitschakelen en je eigen beheerrechten niet intrekken.
                            Je wachtwoord wijzig je via je naam in de kopbalk.
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Box>
                    {!isNew && !isSelf && (
                        <Button onClick={resetPassword} disabled={saving} color="warning">
                            Wachtwoord herstellen
                        </Button>
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button onClick={onClose} disabled={saving}>Annuleren</Button>
                    <Button onClick={save} variant="contained" disabled={!canSave || saving}>
                        {saving ? <CircularProgress size={20} /> : (isNew ? 'Aanmaken' : 'Opslaan')}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}

export default function AccountsPage() {
    const { user } = useAuth();
    const myUsername = typeof user === 'string' ? user : user?.username;
    const [users, setUsers] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);
    const [password, setPassword] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const res = await api.get(endpoints.adminUsers);
            setUsers(res.users);
        } catch (err) {
            setError(errorText(err, 'Accounts ophalen is mislukt.'));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSaved = (saved) => {
        setUsers((list) => {
            const rest = (list || []).filter((u) => u.id !== saved.id);
            const old = (list || []).find((u) => u.id === saved.id);
            return [...rest, { ...old, ...saved }].sort((a, b) =>
                (b.is_active - a.is_active) || a.name.localeCompare(b.name, 'nl'));
        });
        setEditing(null);
    };

    return (
        <AppLayout title="Accounts">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h1" sx={{ fontSize: { xs: '1.4rem', md: '1.8rem' } }}>Accounts</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...EMPTY })} disabled={!users}>
                    Nieuw account
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!users && !error && <CircularProgress />}

            {users && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Naam</TableCell>
                                <TableCell>Gebruikersnaam</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>E-mailadres</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Laatst ingelogd</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u) => (
                                <TableRow key={u.id} hover sx={{ opacity: u.is_active ? 1 : 0.55 }}>
                                    <TableCell>{u.name}</TableCell>
                                    <TableCell>{u.username}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{u.email}</TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatDate(u.last_login)}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                            {!u.is_active && <Chip size="small" label="Uitgeschakeld" />}
                                            {u.is_admin && <Chip size="small" color="primary" label="Beheerder" />}
                                            {u.is_active && u.password_change_required && <Chip size="small" color="warning" label="Tijdelijk wachtwoord" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" aria-label={`${u.username} aanpassen`} onClick={() => setEditing(u)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AccountDialog
                account={editing}
                isSelf={Boolean(editing?.id) && editing?.username === myUsername}
                onClose={() => setEditing(null)}
                onSaved={handleSaved}
                onPassword={setPassword}
            />
            <PasswordDialog info={password} onClose={() => setPassword(null)} />
        </AppLayout>
    );
}
